// PLACEHOLDER CONTENT — Replace with your actual Terms of Service draft.
// To update: edit the sections below or paste your draft HTML/text converted to JSX.

import { motion } from "framer-motion";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import { fadeUp, stagger } from "@/lib/animations";

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="text-xl font-bold text-neutral-900">{title}</h2>
    <div className="mt-3 space-y-3 text-neutral-600">{children}</div>
  </section>
);

const TermsPage = () => {
  usePageSeo(pageSeo.terms);
  return (
    <div className="min-h-screen">
      {/* ── Hero ── */}
      <section className="w-full border-b border-neutral-100 bg-gradient-to-b from-white to-stone-50 py-14">
        <motion.div
          className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 variants={fadeUp} className="text-3xl font-bold text-neutral-900">Terms of Service</motion.h1>
          <motion.p variants={fadeUp} className="mt-3 text-sm text-neutral-500">
            Last updated: 20 May 2026
          </motion.p>
          <motion.p variants={fadeUp} className="mt-4 leading-relaxed text-neutral-600">
            These Terms of Service govern your use of Arsoc's website and products.
            By using our services, you agree to these terms. Please read them carefully.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Body ── */}
      <section className="w-full py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">

            <Section id="acceptance" title="1. Acceptance of terms">
              <p>
                By accessing or using any Arsoc service, website, or product, you agree to
                be bound by these Terms of Service and our{" "}
                <a href="/privacy-policy" className="text-primary underline underline-offset-2 hover:opacity-80">
                  Privacy Policy
                </a>
                . If you do not agree, do not use our services.
              </p>
            </Section>

            <Section id="services" title="2. Services">
              <p>
                Arsoc provides software development services and operates business
                management platforms including Alpha and Estman. The features and scope
                of each product are described on their respective product pages and in
                your service agreement.
              </p>
              <p>
                We reserve the right to modify, suspend, or discontinue any aspect of
                our services with reasonable notice. We will not be liable to you for
                any such modification, suspension, or discontinuation.
              </p>
            </Section>

            <Section id="accounts" title="3. Accounts and access">
              <p>
                To use certain Arsoc products, you or your organisation must create an
                account. You are responsible for maintaining the security of your account
                credentials and for all activity that occurs under your account.
              </p>
              <p>
                You must provide accurate and complete information when creating an
                account. You must promptly notify us at{" "}
                <a href="mailto:admin@arsoc.co.zw" className="text-primary underline underline-offset-2 hover:opacity-80">
                  admin@arsoc.co.zw
                </a>{" "}
                if you become aware of any unauthorised use of your account.
              </p>
            </Section>

            <Section id="acceptable-use" title="4. Acceptable use">
              <p>
                You agree not to use our services in ways that:
              </p>
              <ul className="ml-4 list-disc space-y-1.5">
                <li>Violate any applicable law or regulation</li>
                <li>Infringe the rights of any third party</li>
                <li>Transmit any material that is unlawful, harmful, or fraudulent</li>
                <li>Attempt to gain unauthorised access to our systems or data</li>
                <li>Interfere with the operation of our services</li>
                <li>Use our services to send unsolicited communications</li>
              </ul>
            </Section>

            <Section id="data" title="5. Data and privacy">
              <p>
                Your use of our services is also governed by our{" "}
                <a href="/privacy-policy" className="text-primary underline underline-offset-2 hover:opacity-80">
                  Privacy Policy
                </a>
                , which describes how we collect, use, and protect your data.
              </p>
              <p>
                For data processed on behalf of your organisation (such as end-user data
                in Alpha), you remain the data controller. Arsoc acts as a data processor
                under your instructions.
              </p>
            </Section>

            <Section id="intellectual-property" title="6. Intellectual property">
              <p>
                Arsoc retains all intellectual property rights in our products, software,
                and website content. Nothing in these terms transfers any such rights to you.
              </p>
              <p>
                You retain ownership of the data you input into our products. By using our
                services, you grant Arsoc a limited licence to process that data solely for
                the purpose of providing the services.
              </p>
            </Section>

            <Section id="liability" title="7. Limitation of liability">
              <p>
                To the extent permitted by law, Arsoc shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages arising from your
                use of our services.
              </p>
              <p>
                Our total liability to you for any claim arising from these terms or your
                use of our services shall not exceed the fees paid by you to Arsoc in the
                three months preceding the claim.
              </p>
            </Section>

            <Section id="termination" title="8. Termination">
              <p>
                Either party may terminate a service agreement with reasonable written
                notice, as specified in your service contract. Arsoc may suspend or
                terminate access immediately where there is a material breach of these
                terms.
              </p>
              <p>
                Upon termination, your right to use the relevant services ceases. Data
                handling upon termination is described in our Privacy Policy and your
                service agreement.
              </p>
            </Section>

            <Section id="changes" title="9. Changes to these terms">
              <p>
                We may update these Terms of Service from time to time. We will notify
                you of material changes by updating the "Last updated" date above and,
                where appropriate, by contacting you directly. Continued use of our
                services after changes take effect constitutes acceptance of the updated
                terms.
              </p>
            </Section>

            <Section id="governing-law" title="10. Governing law">
              <p>
                These terms are governed by the laws of Zimbabwe. Any disputes arising
                from these terms or your use of our services shall be subject to the
                jurisdiction of the courts of Zimbabwe.
              </p>
            </Section>

            <Section id="contact" title="11. Contact">
              <p>
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <address className="not-italic rounded-lg border border-neutral-100 bg-stone-50 p-4 text-sm">
                <strong>Arsoc</strong><br />
                150 Nelson Mandela Avenue, Harare, Zimbabwe<br />
                <a
                  href="mailto:admin@arsoc.co.zw"
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  admin@arsoc.co.zw
                </a>
              </address>
            </Section>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
